import React, { useCallback, useState } from 'react';
import { Store, Loader2, Check, ExternalLink } from 'lucide-react';
import { TYPE } from '../../constants/typeScale';
import { setProductionPublished } from '../../services/studioService';
import { useOptionalToast } from '../../contexts/NotificationContext';

/**
 * PUT THIS EPISODE ON YOUR STORE — the control the store slice shipped a route for and nothing called.
 *
 * PUBLISHING IS AN EXPLICIT ACT (RF3-STORE). Nothing reaches a storefront because a render finished, so this
 * is a deliberate button rather than a side effect of completing the journey. The mirror matters as much: a
 * creator who put something out must be able to take it down from the same place, which is why this is a
 * toggle and not a one-way "Publish".
 *
 * IT REFUSES BEFORE IT MISLEADS. There is nothing to show a visitor until a film exists, so without a
 * rendered video the control says what is missing rather than listing an empty page — a store entry with no
 * film is worse than no entry, because someone follows the link.
 *
 * Optimistic, with a revert: the row flips at once and goes back if the server refuses (the store pattern).
 */
export interface PublishToStoreProps {
  storyboardId: string;
  published: boolean;
  /** True once a finished film exists — publishing before that would list an empty page. */
  hasFilm: boolean;
  /** True while any shot in the cut is still a cheap DRAFT take. The server refuses to list those; saying so
   *  here means the creator learns it from the control rather than from a failed publish. */
  hasDrafts?: boolean;
  /** The creator's store handle, so the confirmation can offer the actual address. */
  storeShortId?: string | null;
  onChange: (published: boolean) => void;
}

const PublishToStore: React.FC<PublishToStoreProps> = ({ storyboardId, published, hasFilm, hasDrafts, storeShortId, onChange }) => {
  const [busy, setBusy] = useState(false);
  const { showError } = useOptionalToast();

  const toggle = useCallback(async () => {
    if (busy) return;
    const next = !published;
    setBusy(true);
    onChange(next);                       // optimistic — the state a creator just chose paints immediately
    try {
      await setProductionPublished(storyboardId, next);
    } catch {
      onChange(!next);                    // ...and goes back if the server refused, so nothing looks done that is not
      showError(
        next ? 'Could not publish' : 'Could not take it down',
        'That did not reach the server. Try again in a moment.',
      );
    } finally {
      setBusy(false);
    }
  }, [busy, published, storyboardId, onChange, showError]);

  return (
    <section className="mt-4" aria-label="Your store">
      <div className="flex flex-wrap items-center gap-2">
        <Store className="h-4 w-4 shrink-0 text-secondary-400" aria-hidden />
        <span className={`min-w-0 flex-1 ${TYPE.sectionTitle} text-secondary-900 dark:text-white`}>
          {published ? 'On your store' : 'Put it on your store'}
        </span>
        <button
          type="button"
          onClick={() => void toggle()}
          disabled={busy || (!published && (!hasFilm || !!hasDrafts))}
          data-testid="publish-toggle"
          className={`inline-flex min-h-[34px] shrink-0 items-center gap-2 rounded-lg px-3.5 ${TYPE.control}
                     font-semibold transition-opacity disabled:cursor-not-allowed disabled:opacity-40 ${
                       published
                         ? 'border border-secondary-300 text-secondary-700 dark:border-secondary-600 dark:text-secondary-200'
                         : 'bg-secondary-900 text-white hover:opacity-90 dark:bg-white dark:text-secondary-900'}`}
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : published ? <Check className="h-3.5 w-3.5" /> : <Store className="h-3.5 w-3.5" />}
          {published ? 'Take it down' : 'Publish'}
        </button>
      </div>

      <p className={`mt-1.5 ${TYPE.control} leading-relaxed text-secondary-500 dark:text-secondary-400`}>
        {!hasFilm && !published
          ? 'Render the episode first — a store entry without a film is a link that goes nowhere.'
          : hasDrafts && !published
            ? 'This cut still contains draft shots. Render it at full quality first — a draft is for finding out whether the shots work, not for showing anyone.'
            : published
            ? 'Anyone with your store address can watch this.'
            : 'It appears on your store at the address you can share anywhere. You can take it down at any time.'}
      </p>

      {published && storeShortId && (
        <a
          href={`/s/${storeShortId}`}
          target="_blank"
          rel="noreferrer"
          className={`mt-1.5 inline-flex items-center gap-1.5 ${TYPE.control} text-secondary-600 underline
                     dark:text-secondary-300`}
        >
          <ExternalLink className="h-3.5 w-3.5" /> See it on your store
        </a>
      )}
    </section>
  );
};

export default PublishToStore;
